import { useEffect, useState } from 'react';

import { cx } from '@emotion/css';
import { Button, Col, Form, Row, Space } from 'antd';
import { Formik } from 'formik';
import { isEmpty, includes, isNull } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  resendMfaCode,
  confirmMfaCode,
  doClearConfirmMfaCode,
  doRemoveOfferInvestReferrer,
  setAuthentication,
  doSetLastMfaCodeSentAt,
  doClearResendMfaCode,
} from 'src/actions';
import { DateConstant, ErrorName } from 'src/constants';
import { AuthenticatedUserLoginResponseDto, ErrorResponseDto } from 'src/dtos';
import { useResendMfaCodeCountdown } from 'src/hooks';
import { MInput, MButton, MAlert, RequestErrorMessage } from 'src/lib';
import { BaseStyle, Heading, Spacing } from 'src/styles';
import { formatSecondsCountdown, renderTooManyFailedLoginAttemptsErrorMessage } from 'src/utils';
import * as Yup from 'yup';

import * as Styles from '../BaseLayout.styles';
import { ActiveTab } from '../constants';
import { LogoutButton } from '../LogoutButton';
import { SignUpWithOfferInvestReferrer } from '../SignUpWithOfferInvestReferrer';

export const securityConfirmValidationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Code is required.')
    .min(6, 'Confirmation code should contain 6 numbers')
    .max(6, 'Confirmation code should contain 6 numbers'),
});

export interface MfaConfirmFormValues {
  code: string;
}

export const SecurityConfirmation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorResponseDto>();

  const email = useSelector((state: any) => state.auth.data?.email);
  const password = useSelector((state: any) => state.auth.data?.password);
  const phoneNumber = useSelector((state: any) => state.auth.data?.phoneNumber);
  const lastMfaCodeSentAt: Date | null = useSelector((state: any) => state.app.lastMfaCodeSentAt);

  const confirmMfaError: ErrorResponseDto | undefined = useSelector((state: any) => state.user.confirmMfaCode.error);
  const confirmSucceded = useSelector((state: any) => Boolean(state.user.confirmMfaCode.__succeeded));
  const isConfirmMfaCodeLoading = useSelector((state: any) => Boolean(state.user.confirmMfaCode.__requested));
  const confirmMfaData: AuthenticatedUserLoginResponseDto | undefined = useSelector(
    (state: any) => state.user.confirmMfaCode.data,
  );

  const resendMfaError: ErrorResponseDto | undefined = useSelector((state: any) => state.user.resendMfaCode.error);
  const isResendMfaCodeLoading = useSelector((state: any) => Boolean(state.user.resendMfaCode.__requested));
  const { countdown, setCountdown } = useResendMfaCodeCountdown();

  const hasExceededLoginAttempts = () =>
    includes([ErrorName.TooManyFailedLoginAttemptsError, ErrorName.TooManyLoginAttemptsError], error?.name);

  const renderError = () => {
    if (error?.name === ErrorName.TooManyFailedLoginAttemptsError) {
      return renderTooManyFailedLoginAttemptsErrorMessage();
    }

    return <RequestErrorMessage name={error?.name} message={error?.message} />;
  };

  useEffect(() => {
    if (isNull(lastMfaCodeSentAt)) {
      dispatch(resendMfaCode({ email, password }));

      return;
    }
    const oneMinuteAgo = new Date(Date.now() - DateConstant.ONE_MINUTE);

    if (lastMfaCodeSentAt <= oneMinuteAgo) {
      dispatch(resendMfaCode({ email, password }));
    }
  }, []);

  useEffect(() => {
    if (confirmSucceded && !isEmpty(confirmMfaData)) {
      dispatch(doRemoveOfferInvestReferrer());

      window.gtag('event', 'sign_up_security_confirm_complete');
      const { accessToken, idToken, tbToken } = confirmMfaData;
      const authToken = `Bearer ${accessToken}; idToken ${idToken}; tbToken ${tbToken}`;
      dispatch(setAuthentication({ authToken, tbToken }));
      dispatch(doSetLastMfaCodeSentAt(null));
      navigate(`/complete-account`);

      return;
    }
  }, [confirmSucceded]);

  useEffect(() => {
    setError(confirmMfaError);
  }, [confirmMfaError]);

  useEffect(() => {
    setError(resendMfaError);

    if (resendMfaError) {
      setCountdown(0);
    }
  }, [resendMfaError]);

  useEffect(() => {
    return () => {
      dispatch(doClearConfirmMfaCode());
      dispatch(doClearResendMfaCode());
    };
  }, []);

  return (
    <>
      <SignUpWithOfferInvestReferrer
        activeTab={ActiveTab.Security}
        tabs={[ActiveTab.Register, ActiveTab.Address, ActiveTab.Security]}
        cardTitle='Multi-factor authentication'>
        <Formik<MfaConfirmFormValues>
          initialValues={{
            code: '',
          }}
          enableReinitialize
          validationSchema={securityConfirmValidationSchema}
          validateOnChange
          onSubmit={values => {
            dispatch(confirmMfaCode(values));
            dispatch(doClearResendMfaCode());
          }}>
          {form => {
            return (
              <Form autoComplete='off' style={{ width: '100%' }}>
                {phoneNumber && (
                  <Col span={24} className={Spacing.my16}>
                    <p className={Styles.description}>
                      We sent a six-digit security code to {`XXX - XXX - ${phoneNumber.substr(phoneNumber.length - 4)}`}
                      . Please enter the code in the below box.
                    </p>
                  </Col>
                )}
                <Col span={24} className={Spacing.my16}>
                  <MInput
                    placeholder='Enter code'
                    value={form.values.code}
                    onChange={value => form.setFieldValue('code', value)}
                    disabled={hasExceededLoginAttempts()}
                    error={form.errors.code}
                    name='mfa-confirm-code-name-attribute'
                    autoComplete='off'
                  />
                </Col>
                <Col span={24} className={Styles.resendContainer}>
                  {countdown > 0 ? (
                    <p className={Heading.text1}>
                      You will be able to resend the verification code in {formatSecondsCountdown(countdown)}
                    </p>
                  ) : (
                    <Button
                      className={Styles.resendCodeButton}
                      type='link'
                      disabled={isResendMfaCodeLoading || isConfirmMfaCodeLoading || hasExceededLoginAttempts()}
                      loading={isResendMfaCodeLoading}
                      onClick={() => {
                        form.setFieldValue('code', '');
                        dispatch(resendMfaCode({ email, password }));
                        dispatch(doClearConfirmMfaCode());
                      }}>
                      Resend Code
                    </Button>
                  )}
                </Col>
                {error && (
                  <Col span={24} className={Spacing.my16}>
                    <MAlert type='error' description={renderError()} showIcon closable={false} />
                  </Col>
                )}
                <Space size={8} direction='horizontal' align='center' className={cx(Spacing.my16, BaseStyle.level)}>
                  <MButton
                    type='tertiary'
                    disabled={hasExceededLoginAttempts()}
                    onClick={() => {
                      navigate('/security', {
                        state: {
                          title: 'back-from-confirm',
                        },
                      });
                    }}>
                    Back
                  </MButton>
                  <MButton
                    type='secondary'
                    disabled={
                      isConfirmMfaCodeLoading || isResendMfaCodeLoading || !form.isValid || hasExceededLoginAttempts()
                    }
                    loading={isConfirmMfaCodeLoading}
                    onClick={() => {
                      form.submitForm();
                    }}>
                    Verify
                  </MButton>
                </Space>
              </Form>
            );
          }}
        </Formik>
      </SignUpWithOfferInvestReferrer>
      <Row justify='center' align='middle' className={Spacing.my32}>
        <LogoutButton />
      </Row>
    </>
  );
};
